/**
 * Execution Serializer
 * Converts visual workflow (ReactFlow) to execution-ready format
 */

import { WorkflowNode, WorkflowEdge } from '../serialization/types';
import { ExecutableWorkflow, ExecutableNode, ExecutableEdge } from './types';
import { getAgentByType } from './agentRegistry';

export class ExecutionSerializer {
  /**
   * Convert visual workflow to execution-ready format
   */
  static toExecutableWorkflow(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    metadata: {
      id?: string;
      name: string;
      description?: string;
      version?: number;
    }
  ): ExecutableWorkflow {
    const executableNodes = nodes.map(node => this.convertNode(node, nodes, edges));
    const executableEdges = edges.map(edge => this.convertEdge(edge, nodes));

    return {
      id: metadata.id || '',
      name: metadata.name,
      description: metadata.description,
      version: metadata.version || 1,
      nodes: executableNodes,
      edges: executableEdges,
      configuration: {
        timeout: 3600, // Default 1 hour
        retryPolicy: {
          maxRetries: 3,
          backoffMultiplier: 2
        },
        variables: {},
        environment: {}
      }
    };
  }

  /**
   * Convert a visual node to executable node
   */
  private static convertNode(
    node: WorkflowNode,
    allNodes: WorkflowNode[],
    allEdges: WorkflowEdge[]
  ): ExecutableNode {
    const baseNode: ExecutableNode = {
      id: node.id,
      type: node.type as any,
      label: node.data.label,
      description: node.data.description
    };

    switch (node.type) {
      case 'agent':
        return {
          ...baseNode,
          agentConfig: this.buildAgentConfig(node)
        };

      case 'decision':
        return {
          ...baseNode,
          decisionConfig: this.buildDecisionConfig(node, allEdges)
        };

      case 'loop':
        return {
          ...baseNode,
          loopConfig: this.buildLoopConfig(node, allNodes, allEdges)
        };

      case 'approval':
        return {
          ...baseNode,
          approvalConfig: this.buildApprovalConfig(node)
        };

      default:
        return baseNode;
    }
  }

  /**
   * Build agent execution configuration
   */
  private static buildAgentConfig(node: WorkflowNode) {
    const agentType = node.data.agentType || 'custom';
    const agentDef = getAgentByType(agentType);

    if (!agentDef) {
      // Custom agent - build basic config
      return {
        agentId: node.id,
        agentName: node.data.label,
        agentType: agentType,
        scriptPath: node.data.scriptPath || '',
        workingDirectory: node.data.workingDirectory || '.',
        inputs: node.data.inputs || {},
        outputs: node.data.outputs || {},
        timeout: node.data.timeout || 600,
        environment: node.data.environment || {}
      };
    }

    // Use agent definition from registry and merge with node-specific config
    return {
      ...agentDef.executionConfig,
      agentId: node.id,
      // Override with node-specific values if provided
      timeout: node.data.timeout || agentDef.executionConfig.timeout,
      environment: {
        ...agentDef.executionConfig.environment,
        ...node.data.environment
      },
      // Merge inputs with node-specific input values
      inputs: this.mergeInputs(agentDef.executionConfig.inputs, node.data.inputs || {})
    };
  }

  /**
   * Build decision node configuration
   */
  private static buildDecisionConfig(node: WorkflowNode, allEdges: WorkflowEdge[]) {
    const outgoingEdges = allEdges.filter(edge => edge.source === node.id);
    
    return {
      conditions: (node.data.conditions || []).map((cond: any, index: number) => ({
        id: cond.id || `condition-${index}`,
        expression: cond.expression || 'true',
        targetNodeId: cond.targetNodeId || outgoingEdges[index]?.target || '',
        label: cond.label || `Condition ${index + 1}`,
        variables: cond.variables || {}
      })),
      defaultBranch: node.data.defaultBranch || outgoingEdges[outgoingEdges.length - 1]?.target
    };
  }

  /**
   * Build loop node configuration
   */
  private static buildLoopConfig(
    node: WorkflowNode,
    allNodes: WorkflowNode[],
    allEdges: WorkflowEdge[]
  ) {
    const loopData = node.data.loopConfig || {};
    const outgoingEdges = allEdges.filter(edge => edge.source === node.id);

    return {
      loopType: loopData.loopType || 'forEach',
      iterateOver: loopData.iterateOver,
      whileCondition: loopData.whileCondition,
      count: loopData.count,
      bodyNodeIds: outgoingEdges.map(edge => edge.target),
      maxIterations: loopData.maxIterations || 100,
      breakOnFailure: loopData.breakOnFailure !== false,
      continueOnError: loopData.continueOnError || false,
      iteratorVariableName: loopData.iteratorVariableName || 'currentItem'
    };
  }

  /**
   * Build approval node configuration
   */
  private static buildApprovalConfig(node: WorkflowNode) {
    return {
      approvers: node.data.approvers || [],
      approvalType: node.data.approvalType || 'any',
      timeout: node.data.timeout || 86400, // 24 hours default
      timeoutAction: node.data.timeoutAction || 'escalate',
      escalateTo: node.data.escalateTo || [],
      notificationMessage: node.data.notificationMessage || '',
      notificationChannels: node.data.notificationChannels || ['email']
    };
  }

  /**
   * Convert edge to executable edge
   */
  private static convertEdge(edge: WorkflowEdge, allNodes: WorkflowNode[]): ExecutableEdge {
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      condition: (edge as any).condition,
      label: edge.label,
      dataMapping: (edge as any).dataMapping || {}
    };
  }

  /**
   * Merge agent inputs from registry with node-specific values
   */
  private static mergeInputs(registryInputs: any, nodeInputs: any) {
    const merged = { ...registryInputs };
    
    // Override source values with node-specific values
    Object.keys(nodeInputs).forEach(key => {
      if (merged[key]) {
        merged[key] = {
          ...merged[key],
          source: nodeInputs[key].source || merged[key].source,
          defaultValue: nodeInputs[key].defaultValue !== undefined 
            ? nodeInputs[key].defaultValue 
            : merged[key].defaultValue
        };
      } else {
        merged[key] = nodeInputs[key];
      }
    });

    return merged;
  }
}

